import {
  MaterialReviewList,
  WorkOrder,
  WorkOrderLineItem,
} from "@eljouren/domain";
import { useMemo, useState } from "react";
import { AppButton } from "../../../components/common/buttons/AppButton";
import BankIDLogo from "../../../components/external-brands/bank-id/BankIdLogo";
import ClientUtils from "../../../utils/ClientUtils";

interface Props {
  className?: string;
  list: MaterialReviewList.CustomerType;
  workOrder: WorkOrder.Type;
  isSigning: boolean;
  onSign: () => void;
}

const OrderLineReviewListOverview = (props: Props) => {
  const { className, list, workOrder } = props;

  const [listVerified, setListVerified] = useState(false);

  return (
    <section
      className={ClientUtils.classNames(
        "flex flex-col gap-8 2xs:p-2",
        className
      )}
    >
      <header className="flex flex-col gap-4 py-2">
        <h3 className="text-2xl font-semibold">
          Hej {workOrder.endCustomer.name},
        </h3>
        <p className="">
          Hantverkaren har lagt till orderrader på din arbetsorder{" "}
          <b>#{workOrder.serialNumber}</b>. Vänligen granska och godkänn
          tilläggen innan arbetet påbörjas.
        </p>
      </header>
      <PendingLineItemsSection summary={list.pendingLineItemSummary} />
      <InactivatedLineItemsSection summary={list.inactivatedLineItemSummary} />

      <section className="flex flex-col gap-4 pt-8">
        <h4 className="text-lg">Följande villkor gäller</h4>
        <ul className="col-span-2 flex list-disc flex-col gap-1 pl-4">
          {props.list.userReadableConfirmation.terms.map((term) => (
            <li key={term}>{term}</li>
          ))}
        </ul>
        <footer>
          <div className="grid grid-cols-[auto,minmax(0,1fr)] items-center gap-x-2 gap-y-8 pt-4">
            <input
              type="checkbox"
              id="reviewConfirmedCheckbox"
              className="h-6 w-6 cursor-pointer"
              checked={listVerified}
              onChange={(e) => setListVerified(e.target.checked)}
            />
            <label
              className="ml-2 cursor-pointer"
              htmlFor="reviewConfirmedCheckbox"
            >
              Jag accepterar dessa villkor och är redo att skriva under med
              BankID-appen
            </label>
          </div>
        </footer>
      </section>
      <AppButton
        contentClassName="flex items-center"
        onClick={props.onSign}
        loading={props.isSigning}
        disabled={!listVerified}
      >
        <BankIDLogo size={40} white />
        <span>Skriv under med BankID</span>
      </AppButton>
    </section>
  );
};

function PendingLineItemsSection(props: {
  summary: MaterialReviewList.CustomerType["pendingLineItemSummary"];
}) {
  const summary = props.summary;
  const taxDeduction =
    summary.totalPriceWithoutTaxReduction - summary.totalPriceWithTaxReduction;
  const hasTaxDeduction = taxDeduction > 0;

  return (
    <section className="flex flex-col gap-4">
      <h4 className="text-lg">Sammanställning av orderrader</h4>
      <OverviewLineItemList lineItems={props.summary.lineItems} />
      <dl className="flex flex-col gap-2">
        {hasTaxDeduction && (
          <>
            <div className="flex justify-between gap-2 border-t pt-4">
              <dt className="font-bold">Totalt</dt>
              <dd>{formatPrice(summary.totalPriceWithoutTaxReduction)}</dd>
            </div>
            <div className="flex justify-between gap-2 pb-2">
              <dt>Preliminär skattereduktion</dt>
              <dd>{formatPrice(taxDeduction)}</dd>
            </div>
          </>
        )}
        <div className="flex justify-between gap-2 border-t pt-4 text-lg font-bold">
          <dt>Tilläggskostnad</dt>
          <dd>{formatPrice(summary.totalPriceWithTaxReduction)}</dd>
        </div>
      </dl>
    </section>
  );
}

function InactivatedLineItemsSection(props: {
  summary: MaterialReviewList.CustomerType["inactivatedLineItemSummary"];
}) {
  const summary = props.summary;

  if (!summary.lineItems.length) {
    return <></>;
  }

  return (
    <section className="flex flex-col gap-4 bg-stone-100 p-4">
      <h4 className="text-lg">Borttagna orderrader</h4>
      <p>
        Din hantverkare har tagit bort vissa orderrader du tidigare godkänt.{" "}
        <strong>
          Du kommer inte att behöva betala något för följande orderrader.
        </strong>
      </p>
      <OverviewLineItemList lineItems={summary.lineItems} inactivated />
    </section>
  );
}

function formatPrice(_amount: number | undefined) {
  const amount = _amount ?? 0;
  const str = amount.toFixed(2).replace(".", ",");
  return `${str} SEK`;
}

interface LineItemListProps {
  lineItems: WorkOrderLineItem.CustomerType[];
  inactivated?: boolean;
}

const OverviewLineItemList = (props: LineItemListProps) => {
  const lineItems = useMemo(() => {
    return structuredClone(props.lineItems).sort((a, b) => {
      // Prioritize active items over inactive ones
      if (a.isInactive !== b.isInactive) {
        return a.isInactive ? 1 : -1;
      }

      // If both items are active, sort by createdDate
      return a.createdDate < b.createdDate ? -1 : 1;
    });
  }, [props.lineItems]);

  return (
    <ul className="flex flex-col gap-2 ">
      {lineItems.map((item) => {
        const isInactive = item.isInactive;

        const prices = item.customerPriceDetails;
        const unitPrice = prices.unitPriceIncludingVatWithoutReduction ?? 0;
        const total = unitPrice * item.quantity;

        const name = item.name;

        return (
          <li
            key={item.id}
            className={ClientUtils.classNames(
              "grid grid-cols-[auto_minmax(0,1fr)_auto] gap-2",
              isInactive && "opacity-90"
            )}
          >
            <span className="min-w-7 font-bold">{item.quantity}x</span>
            <section className="flex flex-col">
              <p
                className={ClientUtils.classNames(
                  "break-words",
                  isInactive && "italic"
                )}
              >
                {name}
              </p>
              {!props.inactivated && (
                <p className="text-sm text-gray-600">
                  {formatPrice(prices.unitPriceIncludingVatWithReduction)}
                </p>
              )}
            </section>

            <p
              className={ClientUtils.classNames(
                props.inactivated && "line-through"
              )}
            >
              {formatPrice(total)}
            </p>
          </li>
        );
      })}
    </ul>
  );
};

export default OrderLineReviewListOverview;

/* 
<ul className="flex flex-col gap-2">
          <ExpandablePriceListItem
            label="Nya orderrader"
            price={list.pendingLineItemSummary.totalPriceWithoutTaxReduction}
            openByDefault
            useVerbosePricing
          >
            <OverviewLineItemList
              lineItems={list.pendingLineItemSummary.lineItems}
            />
          </ExpandablePriceListItem>
          {list.previouslyApprovedLineItemSummary.lineItems.length > 0 && (
            <ExpandablePriceListItem
              label="Tidigare godkända orderrader"
              price={
                list.previouslyApprovedLineItemSummary
                  .totalPriceWithoutTaxReduction
              }
              useVerbosePricing
            >
              <OverviewLineItemList
                lineItems={[
                  ...list.previouslyApprovedLineItemSummary.lineItems,
                  ...list.inactivatedLineItemSummary.lineItems,
                ]}
              />
            </ExpandablePriceListItem>
          )}
          {!!list.taxReductionType && list.taxReductionAmount > 0 && (
            <ExpandablePriceListItem
              label={getTaxDeductionLabel(list.taxReductionType)}
              price={-list.taxReductionAmount}
              useVerbosePricing
            />
          )}
          <ExpandablePriceListItem
            label="Total tilläggskostnad"
            final
            price={list.totalPriceWithTaxReduction}
            useVerbosePricing
          />

          {/*  <LineItemSummaryComponent
          summary={list.pendingLineItemSummary}
          type="pending"
        />
        {list.previousLineItemSummary.lineItems.length > 0 && (
          <LineItemSummaryComponent
            summary={list.previousLineItemSummary}
            type="previous"
          />
        )}
        {list.inactivatedLineItemSummary.lineItems.length > 0 && (
          <LineItemSummaryComponent
            summary={list.inactivatedLineItemSummary}
            type="inactivated"
          />
        )} 
          
        </ul>
        */
